import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { IconSet } from '@fcom/ui-components/components/icons/icons.interfaces';

/**
 * Represents a single menu top-level menu item
 */
export interface MenuItem {
  /**
   * The textual representation of the link
   */
  teaserTitle: string;

  /**
   * The URL, which the link should point to
   */
  link?: string;

  /**
   * Secondary textual representation of the link
   */
  linkText?: string;

  /**
   * `true`, if the link points out of the application
   */
  external: boolean;

  /*
   * Only exists for external links
   */
  openInNewTab?: boolean;

  /**
   * Item is current page or a parent of it
   */
  active?: boolean;

  /**
   * Item is current page or a parent of it
   */
  breadcrumb?: boolean;

  /**
   * Possible child items of the menu
   */
  items?: MenuItem[];

  /**
   * Item needs login to be accessed
   */
  loginRequired?: boolean;

  /**
   * Hide item in navigation menu
   */
  hideInMenu?: boolean;

  /**
   * Hide item in section navigation
   */
  hideInSectionNavi?: boolean;

  /**
   * Hide item from the top navigation on mobile
   */
  hideOnMobile?: boolean;

  /**
   * true for paths where the breadcrumb is set by the finnair-com-ux application and not defined by CMS MenuJson
   */
  breadcrumbApplicationOverride?: boolean;

  /**
   * Possible tags for visibility management in navigation
   */
  subjectTaxonomyTags?: string[];

  /**
   * Short label rendered as a tag next to the item, e.g. "New"
   */
  badge?: string;

  /**
   * Identifies if item is a section parent and has child items that are visible in the menu
   */
  hasVisibleChildren?: boolean;

  /**
   * used for fcom-sidebar-nav to allow optional data-testid values
   */
  testid?: string;
}

/**
 * Represents a single breadcrumb item
 */
export interface BreadcrumbItem {
  url: string;
  title: string;
}

/**
 * What sits behind text that a page's hero artwork may extend under (the breadcrumb strip, the
 * hero title). Hero images don't change with the theme, so text on them uses the `static` colour
 * tokens; only text on the plain page surface follows the theme.
 */
export enum HeroBackground {
  /** No hero artwork: the ordinary page surface. */
  NONE = 'none',
  /** A fixed light image (e.g. the gold/silver tier gradient). */
  LIGHT = 'light',
  /** A fixed dark image (e.g. the platinum/lumo tier gradient). */
  DARK = 'dark',
}

/** Text colour for anything sitting on a `HeroBackground`: theme-following primary on the plain
 * surface, static dark/light on a hero image. Each class sits under exactly one key - ngClass
 * toggles per class, so a class listed under two keys would be removed by whichever key is false. */
export const heroBackgroundTextClass = (heroBackground: HeroBackground): Record<string, boolean> => ({
  'nordic-blue-900-text': heroBackground !== HeroBackground.DARK,
  'text-icon-primary': heroBackground === HeroBackground.NONE,
  'text-icon-static-dark': heroBackground === HeroBackground.LIGHT,
  'white-text text-icon-static-light': heroBackground === HeroBackground.DARK,
});

export interface MenuItemWithChildren extends MenuItem {
  /**
   * Possible child items of the menu
   */
  items?: MenuItem[];
}

export type MenuItemIcon = IconSet & {
  position: IconPosition;
};

export interface MenuListItem extends MenuItem {
  withDivider?: boolean;
  id?: string;
  icon?: MenuItemIcon;
  attrs?: {
    'data-testid'?: string;
  };
}

export interface MenuJson {
  /**
   * The "My Trips", "Flight Info", and "Check-in" links.
   * Ignored for now
   */
  top: MenuItemWithChildren[];

  /**
   * Main links.
   */
  main: MenuItemWithChildren[];

  /**
   * Links like "Finnair Holidays", "Finnair Shop" or other country specific entities
   */
  enterprise?: MenuItemWithChildren[];

  /**
   * Placeholder for language content for later use
   */
  bottom: MenuItemWithChildren[];

  /**
   * MenuJson can contain state of the active path, this attribute tells which URL the state is updated for
   */
  currentUrl?: string;

  template: string;
}
